import { format } from "date-fns";
import {
  BookingDetailsDTO,
  OrderDetailsDTO,
} from "./bookingService";
import sendInvoice from "./sendInvoice"; // Import the sendInvoice function
import { useEffect, useState } from "react";
import updateInvoiceStatus from "./updateInvoiceStatus";
import deleteBooking from "./deleteBooking";
import updateBooking from "./updateBooking";
import deleteEntireOrder from "./deleteEntireOrder";
import { useNavigate } from "react-router-dom";
import axios from 'axios'; // Import axios for fetching available chalets


const apiUrl = process.env.REACT_APP_API_URL;

interface BookingUpdateRequest {
  bookingDescription?: string;
  // Add other properties that are required by the updateBooking function
  arrivalDate?: string;
  departureDate?: string;
  totalPrice?: number;
  chaletType?: "big" | "small"; // Add chaletType property
  chaletId?: number; // Add chaletId property
}

interface ChaletDTO {
  chaletId: number; // Correct the property name
  chaletType: number; // Update to number to handle numeric values
  isBlocked: boolean;
  blockedDates: BlockedDateRangeDTO[];
}

interface BlockedDateRangeDTO {
  id: number;
  startDate: Date;
  endDate: Date;
  chaletId: number;
  reasonDescription?: string;
}

interface AvailableChaletsProps {
  chaletType: "big" | "small";
  startDate: string | null;
  endDate: string | null;
  onSelectChalet: (id: number) => void;
  selectedChaletId: number | null; // Add selectedChaletId prop
}

interface NewManageBookingDetailsProps {
  order: OrderDetailsDTO;
  booking?: BookingDetailsDTO;
  onEdit?: (booking: BookingDetailsDTO) => void;
  fetchBookings: () => void; // Add fetchBookings to the props
  onClose?: () => void;
}

const AvailableChalets: React.FC<AvailableChaletsProps> = ({ chaletType, startDate, endDate, onSelectChalet, selectedChaletId }) => {
  const [chalets, setChalets] = useState<ChaletDTO[]>([]);
  const [availabilityInfo, setAvailabilityInfo] = useState<string>("");

  useEffect(() => {
    if (!chaletType || !startDate || !endDate) return; // Ensure chalet options are only fetched after selecting a chalet type

    const fetchChalets = async () => {
      try {
        // Use the new calendar-availability endpoint that properly deduplicates by chaletId
        const response = await axios.get(`${apiUrl}/PackageBooking/calendar-availability`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
            'Content-Type': 'application/json',
          },
          params: {
            startDate,
            endDate
          }
        });
        
        const data = response.data as any;
        console.log("Calendar availability response (NewManageBookingDetails):", data);
        
        if (data && data.availabilityByType) {
          // Find the chalet type the user selected
          const chaletTypeNormalized = chaletType.toLowerCase();
          const typeData = data.availabilityByType.find((a: any) => {
            const backendType = String(a.chaletType).toLowerCase();
            return backendType === chaletTypeNormalized || 
                   (chaletTypeNormalized === "big" && (backendType === "0" || backendType === "big")) ||
                   (chaletTypeNormalized === "small" && (backendType === "1" || backendType === "small"));
          });
          
          if (typeData && typeData.availableChaletIds) {
            // Convert availableChaletIds to ChaletDTO format
            const availableChaletDTOs: ChaletDTO[] = typeData.availableChaletIds.map((id: number) => ({
              chaletId: id,
              chaletType: chaletTypeNormalized === "big" ? 0 : 1,
              isBlocked: false,
              blockedDates: [],
            }));
            
            setChalets(availableChaletDTOs);
            setAvailabilityInfo(`${typeData.availableChalets} of ${typeData.totalChalets} available`);
          } else {
            setChalets([]);
            setAvailabilityInfo("No chalets available");
          }
        } else {
          setChalets([]);
        }
      } catch (error) {
        console.error('Error fetching available chalets:', error);
        setChalets([]);
      }
    };

    fetchChalets();
  }, [chaletType, startDate, endDate]);

  const getChaletTypeLabel = (type: number) => {
    return type === 0 ? "Big" : "Small";
  };

  return (
    <div>
      <h5>Available Chalets {availabilityInfo && <small className="text-muted">({availabilityInfo})</small>}</h5>
      {chalets.length === 0 ? (
        <p className="text-warning">No chalets available for the selected dates and type.</p>
      ) : (
        <ul>
          {chalets.map(chalet => (
            <li key={chalet.chaletId}>
              <button
                onClick={() => onSelectChalet(chalet.chaletId)}
                style={{ backgroundColor: chalet.chaletId === selectedChaletId ? 'lightblue' : 'white' }}
              >
                Chalet {chalet.chaletId} ({getChaletTypeLabel(chalet.chaletType)})
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const NewManageBookingDetails: React.FC<NewManageBookingDetailsProps> = ({
  order,
  booking,
  onEdit,
  onClose,
  fetchBookings, // Add fetchBookings to the props
}) => {
  const [editingBooking, setEditingBooking] =
    useState<BookingDetailsDTO | null>(null);
  const [arrivalDate, setArrivalDate] = useState<string | null>(null);
  const [departureDate, setDepartureDate] = useState<string | null>(null);
  const [invoiceSending, setInvoiceSending] = useState(false);
  const [invoiceError, setInvoiceError] = useState<string | null>(null);
  const [invoiceSuccess, setInvoiceSuccess] = useState<string | null>(null);
  const [invoiceSentAsPdf, setInvoiceSentAsPdf] = useState(false);
  const [invoiceSentAsLink, setInvoiceSentAsLink] = useState(false);
  const [confirmResend, setConfirmResend] = useState(false);
  const [invoiceStatus, setInvoiceStatus] = useState(order.invoiceStatus);
  const [resendType, setResendType] = useState<"pdf" | "link" | null>(null);
   // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [formVisible, setFormVisible] = useState(false);
  const [totalCost, setTotalCost] = useState<number | null>(null);
  const [bookingDescription, setBookingDescription] = useState<string | null>(order.bookingDescription || null);
  const [chaletType, setChaletType] = useState<"big" | "small" | null>(null); // Add state for chaletType
  const [chaletId, setChaletId] = useState<number | null>(null); // Add state for chaletId
 // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const navigate = useNavigate();
   // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const token = localStorage.getItem('accessToken');

  const handleEditBooking = (booking: BookingDetailsDTO) => {
    setEditingBooking(booking);
    setArrivalDate(format(new Date(booking.arrivalDate), "yyyy-MM-dd"));
    setDepartureDate(format(new Date(booking.departureDate), "yyyy-MM-dd"));
    setBookingDescription(order.bookingDescription || ""); // Ensure bookingDescription is set correctly
    setTotalCost(booking.totalCost); // Ensure totalCost is set correctly
    setChaletType(booking.chaletType as "big" | "small" | null); // Ensure chaletType is set correctly
    setChaletId(booking.chaletId || null); // Ensure chaletId is set correctly
  };

  const handleUpdateBooking = async () => {
    if (!editingBooking) return;

    const updateRequest: BookingUpdateRequest = {
      bookingDescription: bookingDescription !== null ? bookingDescription : undefined,
      totalPrice: totalCost !== null ? totalCost : undefined, // Add totalPrice to the payload
      chaletType: chaletType !== null ? chaletType : undefined, // Add chaletType to the payload
      chaletId: chaletId !== null ? chaletId : undefined, // Add chaletId to the payload
    };

    try {
      await updateBooking(editingBooking.bookingId, updateRequest);
      setEditingBooking(null);
      setFormVisible(false);
      alert("Booking updated successfully.");
      fetchBookings(); // Refresh the bookings list
      // Update the local state to reflect the changes
      order.bookingDescription = updateRequest.bookingDescription || order.bookingDescription;
      if (booking) {
        booking.bookingDescription = updateRequest.bookingDescription || booking.bookingDescription;
        booking.totalCost = updateRequest.totalPrice || booking.totalCost;
        booking.chaletType = updateRequest.chaletType || booking.chaletType;
        booking.chaletId = updateRequest.chaletId || booking.chaletId;
      }
    } catch (error) {
      console.error("Error updating booking:", error);
      alert("Failed to update booking.");
    }
  };

  useEffect(() => {
    if (booking) {
      setArrivalDate(format(new Date(booking.arrivalDate), "yyyy-MM-dd"));
      setDepartureDate(format(new Date(booking.departureDate), "yyyy-MM-dd"));
      setBookingDescription(order.bookingDescription || ""); // Ensure bookingDescription is set correctly
      setTotalCost(booking.totalCost);
    }
  }, [booking, order.bookingDescription]);

  const handleDeleteBooking = async (bookingId: number) => {
    if (window.confirm("Are you sure you want to delete this booking?")) {
        try {
            console.log(`Deleting booking with ID: ${bookingId}`);
            await deleteBooking(bookingId);
            alert("Booking deleted successfully.");
            fetchBookings(); // Refresh the bookings list
        } catch (error) {
            console.error("Error deleting booking:", error);
            alert("Failed to delete booking.");
        }
    }
};

const handleDeleteOrder = async (orderNumber: string) => {
    if (window.confirm("Are you sure you want to delete this order?")) {
        try {
            console.log(`Deleting order with number: ${orderNumber}`);
            await deleteEntireOrder(orderNumber);
            alert("Order deleted successfully.");
            fetchBookings(); // Refresh the bookings list
            onClose && onClose();
        } catch (error) {
            console.error("Error deleting order:", error);
            alert("Failed to delete order.");
        }
    }
};
  const transaction =
    booking?.transactions && booking?.transactions.length > 0
      ? booking?.transactions[0]
      : null;
  const formattedAmount = transaction
    ? new Intl.NumberFormat("de-DE", {
        style: "currency",
        currency: "EUR",
      }).format(transaction.amount / 100)
    : null;

  const handleSendInvoice = async (sendAsPdf: boolean) => {
    if ((sendAsPdf && invoiceSentAsPdf) || (!sendAsPdf && invoiceSentAsLink)) {
      if (!confirmResend) {
        setConfirmResend(true);
        setResendType(sendAsPdf ? "pdf" : "link");
        return;
      }
    }

    setInvoiceSending(true);
    setInvoiceError(null);
    setInvoiceSuccess(null);
    setConfirmResend(false);

    try {
      await sendInvoice(order.orderNumber, order.email, sendAsPdf);
      setInvoiceSuccess(
        `Invoice sent successfully as ${sendAsPdf ? "PDF" : "link"}`
      );
      if (sendAsPdf) {
        setInvoiceSentAsPdf(true);
      } else {
        setInvoiceSentAsLink(true);
      }
    } catch (error: any) {
      setInvoiceError(`Error sending invoice: ${error.message}`);
    } finally {
      setInvoiceSending(false);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    console.log("invoice id", order.invoiceId);
    if (!order.invoiceId) {
        console.error("Invoice ID is missing");
        alert("Invoice ID is missing. Cannot update status.");
        return;
    }

    try {
        await updateInvoiceStatus(order.invoiceId, newStatus);
        setInvoiceStatus(newStatus);
        alert("Invoice status updated successfully.");
    } catch (error) {
        console.error("Error updating invoice status:", error);
        alert("Failed to update invoice status.");
    }
  };
  
  const getChaletTypeLabel = (type: "big" | "small" | null) => {
    return type === "big" ? "Big" : type === "small" ? "Small" : "";
  };

  const getChaletTypeFromString = (type: string | null) => {
    if (type === "Big") return "big";
    if (type === "Small") return "small";
    return null;
  };

  return (
    <>
      <section className="booking-customer-details-section">
        {order.bookings.map((booking) => (
          <div key={booking.bookingId} className="booking-detail">
            {booking.mainImageUrl && (
              <div className="booking-banner">
                <img src={booking?.mainImageUrl} alt="Booking" />
              </div>
            )}
            <div className="booking-info">
              <h2>Booking ID: {booking?.bookingId}</h2>
              <p>
                <strong>Order Number:</strong> {order.orderNumber}
              </p>
              <p>
                <strong>Arrival Date:</strong>{" "}
                {format(new Date(booking.arrivalDate), "yyyy-MM-dd")}
              </p>
              <p>
                <strong>Departure Date:</strong>{" "}
                {format(new Date(booking.departureDate), "yyyy-MM-dd")}
              </p>
              <p>
                <strong>Customer Name:</strong> {booking?.customerName}
              </p>
            
                {booking?.companyName && (
                <p>
                  <strong>Company Name:</strong> {booking?.companyName}
                </p>
                
              )}  {booking.preBooking && (
                <p>
                  <strong>Pre Booking:</strong> {booking?.preBooking ? "Yes" : "No"}
                </p>
                
              )}
          
              <p>
                <strong>Total Cost:</strong>{" "}
                {editingBooking && editingBooking?.bookingId === booking?.bookingId ? (
                  <input
                    type="number"
                    value={totalCost !== null ? totalCost : ""}
                    onChange={(e) => setTotalCost(parseFloat(e.target.value))}
                  />
                ) : (
                  new Intl.NumberFormat("de-DE", {
                    style: "currency",
                    currency: "EUR",
                  }).format(booking?.totalCost)
                )}
              </p>
              <p>
                <strong>Package Name:</strong> {booking?.packageName}
              </p>
              <p>
                    <strong>Pricing Option:</strong> {booking.pricingModelDescription}
                    </p>
              <p>
                <strong>Booking Description:</strong>
              </p>
              <div className="booking-description__area">
                {editingBooking &&
                editingBooking.bookingId === booking?.bookingId ? (
                  <>
                    <textarea
                      value={bookingDescription || ""}
                      onChange={(e) => setBookingDescription(e.target.value)}
                    />
                    <select
                      value={chaletType || ""}
                      onChange={(e) => setChaletType(e.target.value as "big" | "small")}
                    >
                      <option value={undefined}>Select Chalet Type</option>
                      <option value="big">Big</option>
                      <option value="small">Small</option>
                    </select>
                    {chaletType && (chaletType === "big" || chaletType === "small") && (
                      <AvailableChalets
                        chaletType={chaletType}
                        startDate={arrivalDate}
                        endDate={departureDate}
                        onSelectChalet={(id) => setChaletId(id)}
                        selectedChaletId={chaletId}
                      />
                    )}
                  
                    {chaletType && (
                      <p>
                        <strong>Selected Chalet Type:</strong> {chaletType}
                      </p>  
                    )}
                    {chaletId && (
                      <p>
                        <strong>Selected Chalet ID:</strong> {chaletId}
                      </p>
                    )}
                  </>
                ) : (
                  <>
                    <span>{order?.bookingDescription}</span>
                 
                    <p>
                      <strong>Chalet Type:</strong> {booking?.chaletType ? getChaletTypeLabel(getChaletTypeFromString(booking?.chaletType)) : ""}
                    </p>
                    <p>
                      <strong>Chalet ID:</strong> {booking?.chaletId}
                    </p>
                  </>
                )}
              </div>
            </div>

            <div className="customer-details">
              <h5>Customer Information</h5>
              <p>
                <strong>Address:</strong> {booking?.address}
              </p>
              {booking?.address2 && (
                <p>
                  <strong>Address 2:</strong> {booking?.address2}
                </p>
              )}
              <p>
                <strong>City:</strong> {booking?.city}
              </p>
              <p>
                <strong>Zip Code:</strong> {booking?.zipCode}
              </p>
              <p>
                <strong>Country:</strong> {booking?.country}
              </p>
              <p>
                <strong>Phone:</strong> {booking?.phone}
              </p>
              <p>
                <strong>Email:</strong> {booking?.email}
              </p>
            </div>

            {transaction && (
              <div className="transaction-details">
                <h5>Transaction Details</h5>
                <p>
                  <strong>Amount:</strong> {formattedAmount}
                </p>
                {transaction.receiptUrl && (
                  <p>
                    <strong>Receipt:</strong>{" "}
                    <a
                      href={transaction.receiptUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View Receipt
                    </a>
                  </p>
                )}
              </div>
            )}

            <div className="booking-actions" style={{ textAlign: "right" }}>
              {editingBooking &&
              editingBooking.bookingId === booking.bookingId ? (
                <button
                  className="btn btn-sm btn-outline-success me-2"
                  onClick={handleUpdateBooking}
                >
                  <i className="fas fa-save"></i> Save
                </button>
              ) : (
                <button
                  className="btn btn-sm btn-outline-primary me-2"
                  onClick={() => handleEditBooking(booking)}
                >
                  <i className="fas fa-edit"></i> Edit
                </button>
              )}
              <button
                className="btn btn-sm btn-outline-danger me-2"
                onClick={() => handleDeleteBooking(booking.bookingId)}
              >
                <i className="fas fa-trash-alt"></i> Delete
              </button>
            </div>
            {editingBooking && editingBooking.bookingId === booking.bookingId && (
              <input
                type="number"
                value={totalCost !== null ? totalCost : ""}
                onChange={(e) => setTotalCost(parseFloat(e.target.value))}
              />
            )}
          </div>
        ))}

        {order.isBusinessUser && (
          <div className="invoice-actions">
            <h5>Send Invoice</h5>
            <button
              className="btn btn-sm btn-outline-primary me-2"
              onClick={() => handleSendInvoice(true)}
              disabled={invoiceSending}
            >
              Send as PDF
            </button>
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => handleSendInvoice(false)}
              disabled={invoiceSending}
            >
              Send as Link
            </button>
            {invoiceSuccess && (
              <p style={{ color: "green" }}>{invoiceSuccess}</p>
            )}
            {invoiceError && <p style={{ color: "red" }}>{invoiceError}</p>}
            {confirmResend && (
              <div className="confirm-resend">
                <p>
                  Invoice has already been sent as {resendType}. Do you want to
                  send it again?
                </p>
                <button
                  className="btn btn-sm btn-outline-primary me-2"
                  onClick={() => handleSendInvoice(resendType === "pdf")}
                  disabled={invoiceSending}
                >
                  Yes, Send as {resendType === "pdf" ? "PDF" : "Link"}
                </button>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setConfirmResend(false)}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        )}
        {order.isBusinessUser && order.invoiceURL && (
          <div className="invoice-info">
            <h5>Invoice Information</h5>
            <p>
              <strong>Invoice Amount:</strong>{" "}
              {new Intl.NumberFormat("de-DE", {
                style: "currency",
                currency: "EUR",
              }).format(order.invoiceAmount ?? 0)}
            </p>
            <p>
              <strong>Invoice Status:</strong> {invoiceStatus}
            </p>
            <div className="container d-flex justify-content-flext-start p-0">
              <button
                className="btn btn-sm btn-outline-primary me-2"
                onClick={() => window.open(order.invoiceURL, "_blank")}
              >
                View Invoice
              </button>

              {/* Dropdown to change invoice status OUTSIDE OF STRIPE SOLUTION I.E. PAID VIA PDF */}
              <div className="dropdown">
                <button
                  className="btn btn-sm btn-outline-primary dropdown-toggle"
                  type="button"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  Change Invoice Status
                </button>
                <ul className="dropdown-menu">
                  <li>
                    <a
                      className="dropdown-item"
                      href="#"
                      onClick={() => handleStatusChange("Pending")}
                    >
                      Pending
                    </a>
                  </li>
                  <li>
                    <a
                      className="dropdown-item"
                      href="#"
                      onClick={() => handleStatusChange("Paid")}
                    >
                      Paid
                    </a>
                  </li>
                  <li>
                    <a
                      className="dropdown-item"
                      href="#"
                      onClick={() => handleStatusChange("Rejected")}
                    >
                      Rejected
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        )}
        <div className="booking-actions">
          <button
            className="btn btn-sm btn-outline-danger me-2"
            onClick={() => handleDeleteOrder(order.orderNumber)}
          >
            <i className="fas fa-trash-alt"></i> Delete Order
          </button>
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={onClose}
          >
            <i className="fas fa-times"></i> Close
          </button>
        </div>
      </section>
    </>
  );
};

export default NewManageBookingDetails;
